import { RouterProvider } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import mixpanel from "mixpanel-browser";

import { router } from "@/app/router";
import { typography, writeTypographyVars } from "@/lib/typography";
import { writeFigmaColorVars } from "@/lib/colors";
import { initializeLanguageForWebView } from "@/lib/i18n-webview-setup";
import { registerClientSource } from "@/lib/client-source";
import { DevAuthProvider } from "@/contexts/dev-auth-context";
import useAuthStore from "@/store/use-auth-store";
import useCreditsStore from "@/store/use-credits-store";
import useUIPreferencesStore from "@/store/use-ui-preferences-store";
import { useCart } from "@/features/cart";
import { usePageVisibility, useUserRefresh } from "@/hooks";
import { useLLMSelectionStore } from "@/features/llm-selection/store/llm-selection-store";
import { useLLMModels } from "@/api/hooks";
import { useAgreementsCheck } from "@/features/agreements";
import { useSiteConfigs } from "@/features/ai-model-selection/hooks/use-site-configs";
import {
  useSpotlightShake,
  useSpotlightShortcut,
} from "@/features/slash-commands";

// Route tanımları ve router bağlamı isteyen global bileşenler `src/app/`
// altında: router.tsx (ağaç), AppRoot.tsx (provider'lar + modal'lar),
// shared-routes.tsx, legacy-redirects.tsx. Modül route'ları `src/modules/`.

//mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN);
mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
  debug: import.meta.env.VITE_APP_ENV === "dev",
  //record_sessions_percent: 100,
  //autocapture: true,
  persistence: "cookie",
  cookie_domain: ".chatharmony.ai",
  api_host: "https://api-eu.mixpanel.com",
  loaded: () => {
    console.log("Mixpanel initialized");
  },
});

// `source` bundan sonraki her event'e super property olarak binsin — widget
// trafiğini web'den ayırabilmemizin FE tarafındaki karşılığı bu. Backend de
// kendi event'lerine `x-source` header'ından aynı değeri yazıyor.
registerClientSource();

function App() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useAuthStore();
  const { refreshUser } = useUserRefresh();
  const { initializeFromAPI } = useUIPreferencesStore();

  // 🎯 Global Spotlight Shortcut (Sağ Shift 2x)
  useSpotlightShortcut();
  // 📱 Mobil: Shake ile Spotlight (sadece İzinler'den manuel aktif edildiyse)
  useSpotlightShake();

  const loadCredits = useCreditsStore((state) => state.loadCredits);

  const { loadCart } = useCart();

  // ✅ LLM Models ve Store
  const { data: llmModels, isSuccess: llmModelsSuccess } = useLLMModels();
  const {
    selectedModelName,
    initializeFromCache,
    selectFirstModel,
    selectModel,
  } = useLLMSelectionStore();

  // ✅ Site Config (defaultModel için) - Sadece user varken çalışsın
  const { data: siteConfigData, isSuccess: siteConfigSuccess } = useSiteConfigs(
    { enabled: !!user?._id },
  );

  const lastLoadedUserIdRef = useRef<string | null>(null);
  const agreementsCheckedForUser = useRef<string | null>(null);
  const { checkAgreements } = useAgreementsCheck();

  // 🔄 Page visibility handling for user data refresh
  usePageVisibility({
    minRefreshInterval: 10000, // 10 seconds interval
  });

  // Mount olduğunda loading'i kapat ve typography & color sistem initialize et
  useEffect(() => {
    setIsLoading(false);

    // Typography CSS variables'ını global olarak set et
    const rootElement = document.documentElement;
    writeTypographyVars(rootElement, typography);

    // Figma Color Variables'ını set et (theme detection ile)
    const isDarkMode = rootElement.classList.contains("dark");
    writeFigmaColorVars(rootElement, isDarkMode ? "dark" : "light");
  }, []);

  // 🌍 WebView Language Detection - Mount olduğunda çalıştır
  useEffect(() => {
    const cleanup = initializeLanguageForWebView();

    // Cleanup function
    return () => {
      if (cleanup && typeof cleanup === "function") {
        cleanup();
      }
    };
  }, []);

  // Sözleşme kontrolü: mevcut hook, yalnızca her login'de bir kez.
  // Persona adımları user'ı güncelleyince tekrar açılmasın.
  useEffect(() => {
    if (!user?._id) {
      agreementsCheckedForUser.current = null;
      return;
    }
    if (!siteConfigSuccess) return;
    if (agreementsCheckedForUser.current === user._id) return;
    agreementsCheckedForUser.current = user._id;
    checkAgreements();
  }, [user?._id, siteConfigSuccess, checkAgreements]);

  // Kullanıcı login olduğunda (veya kullanıcı değiştiğinde) bir kez cart ve credits yükle
  useEffect(() => {
    if (user && user._id && lastLoadedUserIdRef.current !== user._id) {
      lastLoadedUserIdRef.current = user._id;
      loadCredits();
      loadCart();
      // User data'sını refresh et (force refresh to get latest data)
      refreshUser({ silent: true, force: true });

      // 🎨 API'dan tema ve dil tercihlerini initialize et
      initializeFromAPI();
    }
  }, [user, loadCredits, loadCart, refreshUser, initializeFromAPI]);

  // ✅ LLM Model Auto-Selection Logic
  useEffect(() => {
    // User login oldu, LLM models ve site config yüklendi ve henüz model seçilmemişse
    if (
      user?._id &&
      llmModelsSuccess &&
      llmModels?.pages?.length > 0 &&
      siteConfigSuccess &&
      !selectedModelName
    ) {
      // Önce localStorage'dan cache'i yükle
      initializeFromCache();

      // Eğer hala model seçilmemişse (cache'de yoksa), site-config'den defaultModel'i kontrol et
      setTimeout(() => {
        const currentState = useLLMSelectionStore.getState();

        if (!currentState.selectedModelName) {
          const defaultModel = siteConfigData?.data?.defaultModel;

          if (defaultModel && !defaultModel.isDeleted) {
            // Site-config'den gelen defaultModel'i direkt kullan
            selectModel(defaultModel);
          } else {
            // defaultModel yoksa veya silinmişse, ilk modeli seç
            const allModels = llmModels.pages.flatMap((p) => p.data.result);
            selectFirstModel(allModels);
          }
        } else if (!currentState.selectedModel) {
          // selectedModelName var ama model objesi yok - listeden bul ve set et
          const allModels = llmModels.pages.flatMap((p) => p.data.result);
          const cachedModel = allModels.find(
            (model) =>
              model.name === currentState.selectedModelName && !model.isDeleted,
          );

          if (cachedModel) {
            selectModel(cachedModel);
          } else {
            selectFirstModel(allModels);
          }
        }
      }, 100); // Küçük delay ile state update'i bekle
    }
  }, [
    user?._id,
    llmModelsSuccess,
    llmModels,
    siteConfigSuccess,
    siteConfigData,
    selectedModelName,
    initializeFromCache,
    selectFirstModel,
    selectModel,
  ]);

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <p className="text-lg text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  return (
    <DevAuthProvider>
      <RouterProvider router={router} />
    </DevAuthProvider>
  );
}

export default App;
